import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBan,
  faCancel,
  faCircleCheck,
  faEdit,
  faExternalLinkAlt,
  faEye,
  faPeopleGroup,
  faSackDollar,
  faTrashCan
} from '@fortawesome/free-solid-svg-icons';

import { convertTimestampToDateTime } from '../../helper/helper';
import { UserModel, UserRoleEnum, UserStatusEnum } from '../../interfaces/user.interface';
import Modal from '../../components/Common/modal/Modal';
import InlineEditData from '../../components/InlineEditData/InlineEditData';
import AdminTableHeader, { ColumnDef } from '../../components/Admin/AdminTableHeader';
import AdminActionButton from '../../components/Admin/AdminActionButton';

export interface UsersTableProps {
  largeTable?: boolean;
  showUserRole?: boolean;
  showDemoAccess?: boolean;
  showLastSessionData?: boolean;
  allUsers: UserModel[];
  onOptionClick: (option: string) => void;
  onSelectUser: (user: UserModel | undefined) => void;
  handleStatusChange: (id: string | undefined, status: string) => void;
  loggedinUser: UserModel | null;
  handleEditCredits: (id: string | undefined) => void;
  setSelectedUserInfo: (user: UserModel) => void;
  setisUserDetailModalOpen: (isOpen: boolean) => void;
  setOpenDialog: (isOpen: boolean) => void;
  setEmail: (email: string | undefined) => void;
  NoRecordFound: React.FC<{ colSpan: number }>;
  sortOrder: string;
  setSortOrder: (order: string) => void;
  sortBy: string;
  setSortBy: (criteria: string) => void;
  unsubscribeFromPlan?: (userId: string) => void;
  updateUserData?: (email: string, keyProperty: keyof UserModel, value: string) => void;
  handleImpersonateUser?: (user: UserModel) => void;
  onViewCustomerDetails?: (user: UserModel) => void;
}

const UsersTable = ({
                      largeTable,
                      allUsers,
                      showUserRole,
                      showDemoAccess,
                      showLastSessionData,
                      onOptionClick,
                      onSelectUser,
                      handleStatusChange,
                      loggedinUser,
                      handleEditCredits,
                      setSelectedUserInfo,
                      setisUserDetailModalOpen,
                      setOpenDialog,
                      setEmail,
                      NoRecordFound,
                      sortOrder,
                      setSortOrder,
                      sortBy,
                      setSortBy,
                      unsubscribeFromPlan,
                      updateUserData,
                      handleImpersonateUser,
                      onViewCustomerDetails
                    }: UsersTableProps) => {
  const [selectedBook, setSelectedBook] = useState(null);
  const [unsubscribePopup, setUnsubscribePopup] = useState<{ show: boolean, user: UserModel | null }>({
    show: false,
    user: null
  });

  const handleSort = (criteria: string) => {
    if (sortBy === criteria) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(criteria);
      setSortOrder('asc');
    }
  };

  const columns: ColumnDef[] = [
    { key: 'name', label: 'Customer Name', sortable: true, minWidth: '190px' },
    { key: 'email', label: 'Customer Email' },
    { key: 'balance', label: 'Book Balance' },
    { key: 'signup', label: 'Subscribed At', sortable: true },
    ...(showLastSessionData ? [{ key: 'lastSessionAt', label: 'Last Interaction', sortable: true }] : []),
    ...(showDemoAccess ? [{ key: 'demo', label: 'Demo mode' }] : []),
    { key: 'plan', label: 'Plan' },
    { key: 'planExpires', label: 'Plan expires' },
    ...(showUserRole ? [{ key: 'role', label: 'Role' }] : []),
    { key: 'leadId', label: 'Lead ID' },
    { key: 'status', label: 'Status' },
    { key: 'actions', label: 'Actions' },
  ];

  return (
    <>
      {unsubscribePopup && unsubscribePopup?.show && (
        <Modal
          title={'Unsubscribe from Plan'}
          description={`Are you sure you want to unsubscribe ${unsubscribePopup?.user?.email} from the plan?`}
          className={''}
          showSubmitButton={false}
          showCloseButton={false}
          showTopRightCloseButton={false}
          showCloseButtonFunction={() => setUnsubscribePopup({ show: false, user: null })}
        >
          <div className="flex justify-center w-full space-x-4 mt-6 relative">
            <button
              className="bg-storyblueButton disabled:bg-gray-500 text-white font-medium px-6 py-2 rounded-md shadow hover:bg-storyblue transition flex items-center"
              onClick={() => {
                unsubscribeFromPlan && unsubscribeFromPlan(unsubscribePopup?.user?.id!);
                setUnsubscribePopup({ show: false, user: null });
              }}
            >
              Unsubscribe
            </button>

            <button
              className="bg-gray-200 text-gray-700 font-medium px-6 py-2 rounded-md shadow hover:bg-gray-300 transition"
              onClick={() => setUnsubscribePopup({ show: false, user: null })}
            >
              Close
            </button>
          </div>
        </Modal>
      )}
      <div
        className={`w-full overflow-x-auto flex flex-col`}>
        <table className="min-w-full border border-gray-300">
          <AdminTableHeader
            columns={columns}
            sortBy={sortBy}
            sortDirection={sortOrder as 'asc' | 'desc'}
            onSort={handleSort}
          />
          <tbody>
          {allUsers.length > 0 ? (
            allUsers.map((user: UserModel, index: number) => {

              return (
                <tr key={index} className={`text-sm ${index % 2 === 0 ? 'bg-gray-100' : ''}`}>
                  <td
                    className="py-2 text-left px-4 border-b cursor-pointer underline text-storygreen"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onViewCustomerDetails) {
                        setSelectedUserInfo(user);
                        onViewCustomerDetails(user);
                      }
                    }}
                  >
                    <FontAwesomeIcon
                      icon={user?.status === UserStatusEnum.ACTIVE ? faCircleCheck : faBan}
                      className="mr-2"
                      title="Status"
                      style={{
                        color: user?.status === UserStatusEnum.ACTIVE ? 'green' : 'red'
                      }}
                    />
                    {user?.firstName} {user?.lastName}
                    {user?.settings?.USER_SETTING_TEACHER_OR_GROUP && (
                      <FontAwesomeIcon
                        icon={faPeopleGroup}
                        className="ml-2"
                        title="Group"
                        style={{
                          color: 'black'
                        }}
                      />
                    )}
                  </td>
                  <td className="py-2 text-left px-4 border-b">{user?.email}</td>
                  <td className="py-2 text-left px-4 border-b">
                    {+(user?.booksCreateLimit || 0) - +(user?.booksCreatedCounter || 0)}
                  </td>
                  <td className="py-2 text-left px-4 border-b">
                    {convertTimestampToDateTime(user?.timestamp)}
                  </td>
                  {showLastSessionData && <td className="py-2 text-left px-4 border-b">
                    {convertTimestampToDateTime(user?.lastSessionAt)}
                  </td>}
                  {showDemoAccess && <td className="capitalize">{user?.isTrialFreeAccess ? 'yes' : 'no'}</td>}
                  <td>
                    <span className="capitalize">{user?.plan?.name || '-'}</span>
                    {user?.plan && (
                      <FontAwesomeIcon
                        icon={faCancel}
                        className="cursor-pointer ml-2 text-red-500"
                        title="Unsubscribe"
                        onClick={() => {
                          setUnsubscribePopup({ show: true, user: user });
                        }}
                      />
                    )}
                  </td>
                  {/*<td className="py-2 text-left px-4 border-b">{convertTimestampToDateTime(user?.planExpireAt)}</td>*/}
                  <td className="py-2 text-left px-4 border-b">
                    <InlineEditData
                      date={user?.planExpireAt} id={user.email}
                      submit={(id, value) => updateUserData && updateUserData(id, 'planExpireAt', value)}></InlineEditData>
                  </td>
                  {showUserRole && <td className="capitalize">{user?.role?.replace('-', ' ')}</td>}
                  <td className="py-2 text-left px-4 border-b text-xs text-gray-600 truncate max-w-[120px]" title={user?.lead?.leadId || ''}>
                    {user?.lead?.leadId || '—'}
                  </td>
                  <td className="py-2 text-left px-4 border-b">
                    <select
                      value={user?.status ? user?.status : UserStatusEnum.INACTIVE}
                      disabled={loggedinUser?.role !== UserRoleEnum.superAdmin}
                      onChange={(e) => handleStatusChange(user?.email, e.target.value)}
                      className="outline-none"
                    >
                      <option value={UserStatusEnum.ACTIVE}>Active</option>
                      <option value={UserStatusEnum.INACTIVE}>Inactive</option>
                    </select>
                  </td>

                  <td className="py-4 px-4 border-b">
                    <div className="flex gap-3">
                      {loggedinUser?.role === UserRoleEnum.superAdmin && onViewCustomerDetails && (
                        <AdminActionButton
                          icon={faEye}
                          title="View Details"
                          hoverColor="hover:text-teal-600"
                          onClick={() => {
                            setSelectedUserInfo(user);
                            onViewCustomerDetails(user);
                          }}
                        />
                      )}
                      {loggedinUser?.role === UserRoleEnum.superAdmin && (
                        <AdminActionButton
                          icon={faEdit}
                          title="Edit Customer"
                          hoverColor="hover:text-blue-600"
                          onClick={() => {
                            setSelectedUserInfo(user);
                            setisUserDetailModalOpen(true);
                          }}
                        />
                      )}
                      <AdminActionButton
                        icon={faSackDollar}
                        title="Topup Book Credits"
                        hoverColor="hover:text-green-600"
                        onClick={() => {
                          setSelectedBook(null);
                          handleEditCredits(user?.id);
                        }}
                      />
                      {loggedinUser?.role === UserRoleEnum.superAdmin && user?.role !== UserRoleEnum.admin && handleImpersonateUser && (
                        <AdminActionButton
                          icon={faExternalLinkAlt}
                          title="Open as user"
                          hoverColor="hover:text-blue-800"
                          className="text-blue-600"
                          onClick={() => handleImpersonateUser(user)}
                        />
                      )}
                      {loggedinUser?.role === UserRoleEnum.superAdmin && (
                        <AdminActionButton
                          icon={faTrashCan}
                          title="Delete"
                          hoverColor="hover:text-red-600"
                          onClick={() => {
                            setOpenDialog(true);
                            setEmail(user?.email);
                          }}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })
          ) : (
            <NoRecordFound colSpan={8} />
          )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default UsersTable;